import { useState, useEffect, useContext } from 'react'
import moment from 'moment'

import LoadingContext from '../../context/Loading/Loading'
import UserContext from '../../context/User/User'

import { ORGANIZATION_PERMISSION_SLUGS } from '@blocktickets/shared-utils'
import { checkPermission } from '../../utilities/helpers'
import AuthService from '../../utilities/services/auth.service'

import { getOrgEventsForReport, getTimeStampReport } from '../../utilities/api'
import { useReportRefresh } from '../../hooks/useReportRefresh'

import Stack from 'react-bootstrap/Stack'
import Card from 'react-bootstrap/Card'
import Button from 'react-bootstrap/Button'

import { PageLoadingContainer } from '../PageLoadingContainer'
import { NoPermissionsContainer } from '../NoPermissionsContainer'
import { LoadingContainer } from '../LoadingContainer'
import { EmptyContainer } from '../EmptyContainer'
import { Spinner } from '../LoadingContainer/Spinner'
import { ReportActions } from '../ReportActions'
import { EventSelector } from '../EventSelector'
import {
  formatShortDate,
  formatDateTime,
  formatCurrency,
  formatNumber,
  formatString,
  getTimezoneDate,
  timezones,
  createWorkbook,
  addWorksheet,
  setHeaders,
  setColumnsAndRows,
  DEFAULTNUMHEADERS,
  numberFormat,
  currencyFormat,
  saveWorkbook,
} from '../../utilities/helpers'

export default function TimeStampReportWrapper({ organization }) {
  const { isLoading, showLoading, hideLoading } = useContext(LoadingContext)
  const { orgPermissions } = useContext(UserContext)
  const { getPermissions } = AuthService

  const orgPermissionsReady = orgPermissions?.length > 0
  const hasPermission =
    orgPermissionsReady &&
    checkPermission(
      getPermissions(),
      ORGANIZATION_PERMISSION_SLUGS.VIEW_TIME_STAMP_REPORT
    )

  const [events, setEvents] = useState([])

  const [selectedEventUUID, setSelectedEventUUID] = useState('')

  const [startDate, setStartDate] = useState(null)

  const [isLoadingData, setIsLoadingData] = useState(false)
  const { refreshToken, refresh, completeRefresh } =
    useReportRefresh(isLoadingData)

  const [data, setData] = useState(null)

  useEffect(() => {
    if (!orgPermissionsReady) return
    if (!hasPermission) {
      hideLoading()
      return
    }
    showLoading()
    loadEvents(organization.uuid)
  }, [orgPermissionsReady, hasPermission, organization?.uuid])

  useEffect(() => {
    // if there is data and the event or date changes, set data to null to display no data message
    if (data) setData(null)
  }, [selectedEventUUID, startDate])

  const loadEvents = async (organizationUUID) => {
    let res = await getOrgEventsForReport(organizationUUID)

    if (res.status === 200) {
      setEvents(res.data)
      hideLoading()
    }
  }

  const handleFetchReport = async () => {
    if (!selectedEventUUID || !startDate) return

    setIsLoadingData(true)

    try {
      const selectedDate = new Date(startDate)
      const res = await getTimeStampReport(
        organization.uuid,
        selectedEventUUID,
        selectedDate.toISOString()
      )

      setData(res.data)
    } catch (error) {
      console.error('Error fetching time stamp data:', error)
    } finally {
      setIsLoadingData(false)
      completeRefresh()
    }
  }

  useEffect(() => {
    if (!refreshToken) return
    handleFetchReport()
  }, [refreshToken])

  const getButton = () => (
    <Button
      onClick={handleFetchReport}
      className="btn-width btn-width-sm form-control-height"
      disabled={isLoadingData || !selectedEventUUID || !startDate}
    >
      {isLoadingData ? (
        <>
          <Spinner />
        </>
      ) : (
        'Fetch Report'
      )}
    </Button>
  )

  // Universal Excel Generator using JSON schema
  const handleExcelExport = async (
    organizationName,
    event,
    startDate,
    reportData
  ) => {
    const date = `${formatDateTime(moment(startDate))}`

    const workbook = createWorkbook()
    const worksheet = addWorksheet('Time Stamp', workbook)

    const format = (data) => {
      const {
        comp_tickets,
        exchange_tickets,
        gross,
        net,
        sold_tickets,
        total_orders,
        total_tickets,
      } = data

      return {
        total_orders: total_orders,
        total_tickets: total_tickets,
        comp_tickets: comp_tickets,
        exchange_tickets: exchange_tickets,
        sold_tickets: sold_tickets,
        gross: gross,
        net: net,
      }
    }

    const headers = [
      `Event: ${event?.name} - ${formatShortDate(getTimezoneDate(event?.start, timezones(event?.venue?.timezone)), 'dateTime')}`,
      `Date: ${date}`,
    ]

    setHeaders('Time Stamp Report', organizationName, worksheet, headers)

    setColumnsAndRows(
      Object.keys(format(reportData)),
      [reportData],
      format,
      worksheet
    )

    const headerRows = DEFAULTNUMHEADERS + headers?.length

    numberFormat(1, worksheet, headerRows)
    numberFormat(2, worksheet, headerRows)
    numberFormat(3, worksheet, headerRows)
    numberFormat(4, worksheet, headerRows)
    numberFormat(5, worksheet, headerRows)
    currencyFormat(6, worksheet, headerRows)
    currencyFormat(7, worksheet, headerRows)

    // Generate and download Excel file
    // save excel worksheet
    saveWorkbook(workbook, 'time stamp report')
      .then()
      .catch((err) => alert(err.message))
  }

  if (!orgPermissionsReady) {
    return <PageLoadingContainer style="without-sidebar" />
  }

  if (!hasPermission) {
    return (
      <div className="spacer-sm">
        <NoPermissionsContainer />
      </div>
    )
  }

  if (isLoading) {
    return <PageLoadingContainer style="without-sidebar" />
  }

  return (
    <>
      <section className="spacer-sm" data-testid="time-stamp-report-section">
        <header className="section-header-sm">
          <div className="section-heading">
            <h1>Time Stamp Report</h1>
          </div>
          <p className="section-header-desc">
            View all orders, tickets sold, and revenue recorded after the
            selected date and time.
          </p>
        </header>
        {events?.length > 0 ? (
          <>
            <ReportActions
              isLoading={isLoadingData}
              onRefresh={refresh}
              isRefreshing={isLoadingData}
              selected={selectedEventUUID}
              startDate={startDate}
              setStartDate={setStartDate}
              data={data}
              getButton={getButton}
              downloadReport={() =>
                handleExcelExport(
                  organization?.name,
                  events?.find((event) => event?.uuid === selectedEventUUID),
                  startDate,
                  data?.summary
                )
              }
            >
              <EventSelector
                setSelected={setSelectedEventUUID}
                selected={selectedEventUUID}
                events={events}
                isLoading={isLoadingData}
                groupBySeason
                showYear
              />
            </ReportActions>
            <Results
              isLoading={isLoadingData}
              selectedEvent={selectedEventUUID}
              startDate={startDate}
              data={data}
            />
          </>
        ) : (
          <Card body>
            <EmptyContainer style="center lg report">
              <p>No events created</p>
              <p className="subtitle subtitle--dark">
                Create an event to start tracking its data.
              </p>
            </EmptyContainer>
          </Card>
        )}
      </section>
    </>
  )
}

const getValue = (title, value) => {
  switch (title) {
    case 'gross':
    case 'net':
      return formatCurrency(value)

    default:
      return formatNumber(value)
  }
}

function Report({ report }) {
  return (
    <Stack direction="horizontal" gap={3}>
      {Object.entries(report?.summary).map(([key, value], idx) => (
        <Card
          key={idx}
          className="card--dark card-with-border card--sm"
          style={{ flexBasis: '176px' }}
        >
          <Card.Body className="d-flex-column gap-2 align-items-center">
            <Card.Title as="h5" className="fw-normal text-nowrap">
              {formatString(key)}
            </Card.Title>
            <span className="m-0 fs-md fs-md-bold">{getValue(key, value)}</span>
          </Card.Body>
        </Card>
      ))}
    </Stack>
  )
}

function Results({ isLoading, selectedEvent, startDate, data }) {
  if (!data || !selectedEvent || !startDate) {
    return (
      <Card body>
        <EmptyContainer style="center lg report">
          <p>Select an event and a date/time to view its revenue</p>
          <p className="subtitle subtitle--dark">
            Select an event and a date/time to generate data for that specific
            date/time. Then press Fetch Report to see the generated data.
          </p>
        </EmptyContainer>
      </Card>
    )
  }

  if (isLoading) {
    return <LoadingContainer />
  }

  if (data?.length === 0) {
    return (
      <Card body>
        <EmptyContainer style="center lg report">
          <p>No data</p>
          <p className="subtitle subtitle--dark">
            There's currently no data for the specified date and time. Please
            select a different date and time.
          </p>
        </EmptyContainer>
      </Card>
    )
  }

  return (
    <Card body className="card--md">
      <div className="card-body-heading card-body-heading--xs">
        <Card.Title as="h5">Summary Report</Card.Title>
      </div>
      <Report report={data} />
    </Card>
  )
}
